import type { ReactNode } from "react"
import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import {
  getContactStatusConfig,
  getContactHealthyDisplay,
  CONTACT_SYNC_STATUS_EMAIL_MISMATCH,
} from "@/v2-backoffice-client/user/contact/userContact.constants"

export type UserContactBadgeProps = {
  user: UserFragment
  // When true the healthy "In sync" / "Not applicable" state is rendered
  // as a plain dot too (no tooltip) instead of null.
  showHealthy?: boolean
  // Optional repair affordance rendered at the foot of the tooltip (the
  // cell passes the "Sync to HubSpot" button here so the action sits next
  // to the explanation). Tooltip content is hoverable, so it stays
  // clickable. Only shows when there's a problem tooltip to attach to.
  action?: ReactNode
}

// UserContactBadge surfaces how a user relates to their HubSpot contact
// (computed live from the acc.UserContacts link row) — the CRM sibling of
// UserIdentityBadge. The tooltip explains the divergence and which repair
// action to use.
function UserContactBadge(props: UserContactBadgeProps) {
  const { user, showHealthy, action } = props

  const config = getContactStatusConfig(user.ContactSyncStatus)

  if (!config) {
    if (!showHealthy) return null
    const healthy = getContactHealthyDisplay(user.ContactSyncStatus)
    return <DotText variant={healthy.variant}>{healthy.label}</DotText>
  }

  const emailMismatch =
    (user.ContactSyncStatus as unknown as string) ===
    CONTACT_SYNC_STATUS_EMAIL_MISMATCH

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant={config.variant}>{config.label}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-3 p-3">
          <div className="flex flex-col gap-1">
            <DotText
              variant={config.variant}
              className="flex font-medium text-popover-foreground"
            >
              {config.label}
            </DotText>
            <p className="text-muted-foreground">{config.description}</p>

            {emailMismatch && (
              <div className="mt-1 flex flex-col gap-0.5 text-xs">
                <span>
                  <span className="text-muted-foreground">Database: </span>
                  {user.Email}
                </span>
                <span>
                  <span className="text-muted-foreground">HubSpot: </span>
                  {user.Contact?.Email}
                </span>
              </div>
            )}

            <p className="mt-1 text-xs text-muted-foreground">{config.fix}</p>
          </div>

          {action && (
            <div className="border-t border-border pt-2">{action}</div>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default UserContactBadge
